import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Radio from "@material-ui/core/Radio";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Box from "@material-ui/core/Box";

import MergeTypeOutlinedIcon from "@material-ui/icons/MergeTypeOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import ConfirmationModal from "../../components/ConfirmationModal";
import { displayClientName } from "../../utils/clientName";

/**
 * Revisão de clientes possivelmente duplicados.
 *
 * O backend já agrupa por sinal (CPF, CNPJ, e-mail, telefone, nome parecido)
 * e sugere o registro mais recente como o que deve ficar — aqui só se
 * confirma ou troca essa escolha antes de disparar a mesclagem, porque
 * juntar cadastros é destrutivo (os outros são apagados) e não pode
 * acontecer sem alguém olhar antes.
 */

const useStyles = makeStyles((theme) => ({
  groupPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  motivos: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  emptyBox: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "dd/MM/yyyy");
  } catch {
    return "-";
  }
};

const displayDoc = (client) => {
  if (client.personType === "PJ") return client.cnpj || "-";
  return client.cpf || "-";
};

const buildDefaultSelection = (group) => {
  const keepId = group.clients[0].id;
  const mergeIds = new Set(
    group.clients.slice(1).map((c) => c.id)
  );
  return { keepId, mergeIds };
};

const ClientDuplicates = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selections, setSelections] = useState({});
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [merging, setMerging] = useState(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients/duplicates");
      setGroups(data);
      const nextSelections = {};
      data.forEach((group, idx) => {
        nextSelections[idx] = buildDefaultSelection(group);
      });
      setSelections(nextSelections);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleKeepChange = (idx, clientId) => {
    setSelections((prev) => {
      const group = groups[idx];
      const mergeIds = new Set(
        group.clients.map((c) => c.id).filter((id) => id !== clientId)
      );
      return { ...prev, [idx]: { keepId: clientId, mergeIds } };
    });
  };

  const handleToggleMerge = (idx, clientId) => {
    setSelections((prev) => {
      const current = prev[idx];
      const mergeIds = new Set(current.mergeIds);
      if (mergeIds.has(clientId)) mergeIds.delete(clientId);
      else mergeIds.add(clientId);
      return { ...prev, [idx]: { ...current, mergeIds } };
    });
  };

  const handleConfirmMerge = async () => {
    const idx = confirmIndex;
    const selection = selections[idx];
    if (!selection || !selection.mergeIds.size) return;
    setMerging(true);
    try {
      await api.post("/clients/merge", {
        keepId: selection.keepId,
        mergeIds: [...selection.mergeIds],
      });
      toast.success("Clientes mesclados com sucesso!");
      await fetchGroups();
    } catch (err) {
      toastError(err);
    } finally {
      setMerging(false);
      setConfirmIndex(null);
    }
  };

  const confirmGroup = confirmIndex !== null ? groups[confirmIndex] : null;
  const confirmSelection = confirmIndex !== null ? selections[confirmIndex] : null;

  return (
    <MainContainer>
      <PageHeader
        title="Duplicados"
        count={loading ? undefined : groups.length}
        subtitle="Clientes que provavelmente são o mesmo cadastro repetido, agrupados por CPF/CNPJ, e-mail, telefone ou nome parecido. Escolha qual registro fica — os outros têm o histórico movido para ele e são apagados."
        actions={
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchGroups}
            disabled={loading}
          >
            Atualizar
          </Button>
        }
      />

      {loading && (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      )}

      {!loading && groups.length === 0 && (
        <Box className={classes.emptyBox}>
          <Typography variant="body1">
            Nenhum duplicado encontrado no momento.
          </Typography>
        </Box>
      )}

      {!loading &&
        groups.map((group, idx) => {
          const selection = selections[idx];
          if (!selection) return null;
          return (
            <Paper key={group.clients.map((c) => c.id).join("-")} className={classes.groupPaper} variant="outlined">
              <div className={classes.groupHeader}>
                <div className={classes.motivos}>
                  {group.motivos.map((motivo) => (
                    <Chip key={motivo} label={motivo} size="small" />
                  ))}
                  <Typography variant="body2" className={classes.muted}>
                    {group.clients.length} registros
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<MergeTypeOutlinedIcon />}
                  disabled={!selection.mergeIds.size || merging}
                  onClick={() => setConfirmIndex(idx)}
                >
                  Mesclar selecionados
                </Button>
              </div>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Manter</TableCell>
                    <TableCell padding="checkbox">Mesclar</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Documento</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Negócios</TableCell>
                    <TableCell align="right">Tickets</TableCell>
                    <TableCell>Criado em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.clients.map((client) => {
                    const isKeeper = selection.keepId === client.id;
                    return (
                      <TableRow key={client.id} selected={isKeeper}>
                        <TableCell padding="checkbox">
                          <Radio
                            checked={isKeeper}
                            onChange={() => handleKeepChange(idx, client.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={!isKeeper && selection.mergeIds.has(client.id)}
                            disabled={isKeeper}
                            onChange={() => handleToggleMerge(idx, client.id)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{displayClientName(client)}</TableCell>
                        <TableCell>{displayDoc(client)}</TableCell>
                        <TableCell>{client.email || "-"}</TableCell>
                        <TableCell>{client.mobilePhone || client.phone || "-"}</TableCell>
                        <TableCell align="right">{client.dealCount}</TableCell>
                        <TableCell align="right">{client.ticketCount}</TableCell>
                        <TableCell>{formatDate(client.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          );
        })}

      <ConfirmationModal
        title="Mesclar clientes?"
        open={confirmIndex !== null}
        onClose={() => setConfirmIndex(null)}
        onConfirm={handleConfirmMerge}
      >
        {confirmGroup && confirmSelection && (
          <>
            Todo o histórico (negócios, tickets, contas a receber, contratos
            etc.) de {confirmSelection.mergeIds.size} registro(s) será movido
            para{" "}
            <strong>
              {displayClientName(
                confirmGroup.clients.find((c) => c.id === confirmSelection.keepId)
              )}
            </strong>
            , e esses registros serão apagados. Esta ação não pode ser
            desfeita.
          </>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default ClientDuplicates;
